import React from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import rehypeRaw from "rehype-raw";

import LinkIcon from "atoms/icon/icons/pdp_link_icon.svg?url";

import { createPdpLinkHandler } from "../../adonis.helpers";
import "./MarkdownContent.css";

export interface MarkdownContentProps {
  content: string;
}

// Link Icon Components
const GENERIC_LINK_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="m9 7 5-5M14 2v5h-5M7 9l-5 5M2 14v-5h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const PdpLinkIcon = () => (
  <img
    src={LinkIcon}
    alt="link"
    style={{ display: "inline", paddingBottom: "2px" }}
  />
);

const GenericLinkIcon = () => (
  <img
    src={`data:image/svg+xml,${encodeURIComponent(GENERIC_LINK_SVG)}`}
    alt="link"
    style={{ display: "inline", paddingBottom: "2px" }}
  />
);

// Custom Link Component for Markdown
interface LinkProps extends React.ComponentProps<"a"> {
  node?: unknown;
}

const CustomLinkComponent: React.FC<LinkProps> = ({
  href,
  children,
  ...props
}) => {
  const navigate = useNavigate();

  // Check if this is a PDP link that needs special handling
  if (href && /^\/pdp\/package\/[^"$]+$/.exec(href)) {
    const linkHandler = createPdpLinkHandler(navigate);
    const linkId = linkHandler(href);

    const handleClick = () => {
      if (linkId && typeof window !== "undefined") {
        // @ts-expect-error - pdpNavigate is not defined in the window object
        window.pdpNavigate?.[linkId]?.();
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{
          color: "blue",
          fontWeight: "bold",
          textDecoration: "underline",
          cursor: "pointer",
          background: "transparent",
          border: "none",
          padding: "0",
          margin: "0",
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: "inherit",
        }}
        className="treatment-link"
        aria-label={`Navigate to treatment: ${children}`}
      >
        <PdpLinkIcon /> {children}:
      </button>
    );
  }

  // For non-PDP links, render normally
  return (
    <a href={href || "#"} target="_blank" rel="noopener noreferrer" {...props}>
      <GenericLinkIcon /> {children}
    </a>
  );
};

/**
 * MarkdownContent component for rendering markdown content with custom link handling.
 * Uses ReactMarkdown with rehype-raw for AI assistant messages.
 * Handles both PDP links (internal navigation) and external links.
 */
export const MarkdownContent = ({ content }: MarkdownContentProps) => {
  return (
    <div className="markdown-content">
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        components={{
          a: (props) => <CustomLinkComponent {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
